/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'

import { basketItemsWithProductInfoAtom } from '@/store/basket/atoms'
import { LineItemPrice } from '@/store/basket/types'
import { selectedCountryAtom } from '@/store/country'
import { getPricingZoneByCountryCode } from '@/utils/pricing-zones'

/**
 * Atom family to calculate the price for a specific basket item.
 * Pass an object { productId: string, variantId?: string } to get the specific atom instance.
 */
const itemPriceAtomFamily = atomFamily(
  (params: { productId: string; variantId?: string }) =>
    atom<LineItemPrice | null>((get) => {
      const itemsWithDetails = get(basketItemsWithProductInfoAtom)
      const selectedCountry = get(selectedCountryAtom)
      const item = itemsWithDetails.find((i) => i.productId === params.productId && i.variantId === params.variantId)

      if (!item || !item.details) {
        if (item) {
          console.warn(`Details missing for item: ${params.productId}, variant: ${params.variantId}`)
        } else {
          console.warn(`Item not found in basket details: ${params.productId}, variant: ${params.variantId}`)
        }
        return null
      }

      const kitData = item.details
      const quantity = item.quantity

      let calculatedPrice: { amount: number; currency: 'GBP' | 'USD' } | null = null

      const currentZone = selectedCountry ? getPricingZoneByCountryCode(selectedCountry.code) : null
      if (currentZone && kitData.zone_pricing && kitData.zone_pricing.length > 0) {
        const zonePriceInfo = kitData.zone_pricing.find((p) => p.zone === currentZone)
        if (zonePriceInfo && zonePriceInfo.amount) {
          const amount = parseFloat(zonePriceInfo.amount)
          if (!isNaN(amount)) {
            const currency = zonePriceInfo.currency === 'USD' ? 'USD' : 'GBP'
            calculatedPrice = { amount, currency }
          }
        }
      }

      if (!calculatedPrice && kitData.default_pricing) {
        const defaultPriceInfo = kitData.default_pricing
        if (defaultPriceInfo && defaultPriceInfo.amount) {
          const amount = parseFloat(defaultPriceInfo.amount)
          if (!isNaN(amount)) {
            calculatedPrice = { amount, currency: 'GBP' }
          }
        }
      }

      if (!calculatedPrice || isNaN(calculatedPrice.amount)) {
        console.warn(`Could not determine price for item: ${item.productId} variant: ${item.variantId}`)
        return null
      }

      return {
        currency: calculatedPrice.currency,
        unitPrice: calculatedPrice.amount,
        lineTotal: calculatedPrice.amount * quantity,
      }
    }),
  (a, b) => a.productId === b.productId && a.variantId === b.variantId,
)

/** Derived atom used to determine which currency the basket is using. */
const currencyAtom = atom<'GBP' | 'USD' | null>((get) => {
  let currency: 'GBP' | 'USD' | null = null
  const items = get(basketItemsWithProductInfoAtom)
  const item = items[0]
  if (items) {
    const priceInfo = get(itemPriceAtomFamily({ productId: item.productId, variantId: item.variantId }))
    if (priceInfo) {
      currency = priceInfo.currency
    }
  }

  return currency
})

/** Derived atom for the basket subtotal. */
const subtotalAtom = atom<{ amount: number; currency: 'GBP' | 'USD' } | null>((get) => {
  const items = get(basketItemsWithProductInfoAtom)
  let total = 0
  let currency: 'GBP' | 'USD' | null = null

  for (const item of items) {
    const priceInfo = get(itemPriceAtomFamily({ productId: item.productId, variantId: item.variantId }))
    if (priceInfo) {
      if (currency === null) {
        currency = priceInfo.currency
      }

      total += priceInfo.lineTotal
    }
  }

  if (currency === null) {
    return null
  }

  return { amount: total, currency }
})

/** Derived atom for calculating tax. Currently, only UK orders are charged tax (VAT). */
const taxAtom = atom<{ amount: number; currency: 'GBP' | 'USD' } | null>((get) => {
  const selectedCountry = get(selectedCountryAtom)
  const subtotal = get(subtotalAtom)

  if (!subtotal) return null

  let taxAmount = 0
  if (selectedCountry.code === 'GB') {
    taxAmount = subtotal.amount * 0.2
  }

  return { amount: taxAmount, currency: subtotal.currency }
})

/** Derived atom for the final total (subtotal + tax). */
const totalAtom = atom<{ amount: number; currency: 'GBP' | 'USD' } | null>((get) => {
  const subtotal = get(subtotalAtom)
  const tax = get(taxAtom)

  if (!subtotal) return null
  const taxAmount = tax ? tax.amount : 0

  return {
    amount: subtotal.amount + taxAmount,
    currency: subtotal.currency,
  }
})

/** Atom debug labels. */
currencyAtom.debugLabel = 'currencyAtom'
subtotalAtom.debugLabel = 'subtotalAtom'
taxAtom.debugLabel = 'taxAtom'
totalAtom.debugLabel = 'totalAtom'

export { currencyAtom, itemPriceAtomFamily, subtotalAtom, taxAtom, totalAtom }
